import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Tabs, 
  Tab, 
  useTheme, 
  Divider,
  Chip,
  TextField,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Stack,
  Paper,
  Switch,
  FormControlLabel,
  Autocomplete,
  Button
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import BusinessIcon from '@mui/icons-material/Business';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { ReportContext, SocketContext } from '../Context';
import axios from 'axios';

const HEADER_HEIGHT = 48;

const SECTIONS = [
  { id: 'infos', label: 'Informations générales' },
  { id: 'destinataires', label: 'Destinataires' },
  { id: 'sujet', label: 'Sujet' },
  { id: 'avis-magistrat', label: 'Avis Magistrat' },
  { id: 'privations', label: 'Privations de liberté' },
  { id: 'faits', label: 'Faits' },
  { id: 'parties', label: 'Parties concernées' },
  { id: 'sections-custom', label: 'Sections personnalisables' },
];

function CoverSheet() {
  const theme = useTheme();
  const { report } = useContext(ReportContext);
  const { socket } = useContext(SocketContext);
  const sections = useMemo(() => SECTIONS, []);
  const headerRef = useRef(null);
  const [activeId, setActiveId] = useState(sections[0].id);
  const [progress, setProgress] = useState(0);
  
  // États pour les champs de la section Informations générales
  const [isUrgent, setIsUrgent] = useState(false);
  const [isParPorteur, setIsParPorteur] = useState(false);
  const [isMineurEnCause, setIsMineurEnCause] = useState(false);
  const [dateTransmission, setDateTransmission] = useState('');
  const [nombreAnnexes, setNombreAnnexes] = useState(0);
  const [phenomene, setPhenomene] = useState('');
  const [mentionPersonnelle, setMentionPersonnelle] = useState('');
  const [phenomenes, setPhenomenes] = useState([]);
  const [registres, setRegistres] = useState([]);
  const [selectedRegistre, setSelectedRegistre] = useState('');
  const [types, setTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('');

  // États pour la section Destinataires
  const [recipients, setRecipients] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [customRecipientName, setCustomRecipientName] = useState('');
  const [autocompleteKey, setAutocompleteKey] = useState(0);

  // États pour la section Sujet
  const [subjects, setSubjects] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [customSubjectName, setCustomSubjectName] = useState('');
  const [autocompleteKeySubject, setAutocompleteKeySubject] = useState(0);

  // États pour la section Privations de liberté
  const [privationsLibertes, setPrivationsLibertes] = useState([]);
  const [personnesPhysiques, setPersonnesPhysiques] = useState([]);
  const [selectedPersonneId, setSelectedPersonneId] = useState('');
  const [dateHeurePrivation, setDateHeurePrivation] = useState('');
  const [typePrivation, setTypePrivation] = useState('');

  // États pour la section Sections personnalisables
  const [sectionsPersonnalisees, setSectionsPersonnalisees] = useState([]);
  const [titreSection, setTitreSection] = useState('');
  const [contenuSection, setContenuSection] = useState('');
  const [editingSectionId, setEditingSectionId] = useState(null);

  // États pour la section Parties concernées
  const [relationSuspectVictime, setRelationSuspectVictime] = useState('');
  const [personnesMorales, setPersonnesMorales] = useState([]);
  const [selectedPersonnesPhysiques, setSelectedPersonnesPhysiques] = useState({});
  const [selectedPersonnesMorales, setSelectedPersonnesMorales] = useState({});

  // États pour la section Avis Magistrat
  const [magistrats, setMagistrats] = useState([]);
  const [avisMagistrats, setAvisMagistrats] = useState([]);
  const [selectedMagistratId, setSelectedMagistratId] = useState('');
  const [customMagistratInfo, setCustomMagistratInfo] = useState('');
  const [customMagistratGenre, setCustomMagistratGenre] = useState('homme');
  const [dateHeureAvis, setDateHeureAvis] = useState('');
  const [autocompleteMagistratKey, setAutocompleteMagistratKey] = useState(0);

  // États pour afficher/masquer les sections optionnelles
  const [showSujet, setShowSujet] = useState(true);
  const [showAvisMagistrat, setShowAvisMagistrat] = useState(true);
  const [showPrivations, setShowPrivations] = useState(true);

  // États pour la section Faits
  const [faits, setFaits] = useState([]);
  const [selectedFaits, setSelectedFaits] = useState({});

  // État pour les données du rapport
  const [reportData, setReportData] = useState(null);
  const lastSyncedSnapshotRef = useRef(null);

  const cloneArray = (arr) =>
    Array.isArray(arr)
      ? arr.map((item) =>
          typeof item === 'object' && item !== null ? { ...item } : item
        )
      : [];

  const toPlainObject = (value) => {
    if (value instanceof Map) {
      return Object.fromEntries(value);
    }
    if (value && typeof value === 'object') {
      return { ...value };
    }
    return {};
  };

  const normalizeCoversheetPayload = (source = {}) => {
    const defaultRegistreId = registres.length > 0 ? registres[0].id : '';
    const defaultTypeId = types.length > 0 ? types[0].id : '';

    return {
      isUrgent: !!source.isUrgent,
      isParPorteur: !!source.isParPorteur,
      isMineurEnCause: !!source.isMineurEnCause,
      dateTransmission: source.dateTransmission || '',
      nombreAnnexes:
        typeof source.nombreAnnexes === 'number'
          ? source.nombreAnnexes
          : Number(source.nombreAnnexes) || 0,
      phenomene: source.phenomene || '',
      mentionPersonnelle: source.mentionPersonnelle || '',
      selectedRegistre:
        source.selectedRegistre !== undefined
          ? source.selectedRegistre
          : defaultRegistreId,
      selectedType:
        source.selectedType !== undefined ? source.selectedType : defaultTypeId,
      selectedRecipients: cloneArray(source.selectedRecipients),
      selectedSubjects: cloneArray(source.selectedSubjects),
      avisMagistrats: cloneArray(source.avisMagistrats),
      privationsLibertes: cloneArray(source.privationsLibertes),
      selectedFaits: toPlainObject(source.selectedFaits),
      relationSuspectVictime: source.relationSuspectVictime || '',
      selectedPersonnesPhysiques: toPlainObject(
        source.selectedPersonnesPhysiques
      ),
      selectedPersonnesMorales: toPlainObject(
        source.selectedPersonnesMorales
      ),
      sectionsPersonnalisees: cloneArray(source.sectionsPersonnalisees),
      showSujet:
        typeof source.showSujet === 'boolean' ? source.showSujet : true,
      showAvisMagistrat:
        typeof source.showAvisMagistrat === 'boolean'
          ? source.showAvisMagistrat
          : true,
      showPrivations:
        typeof source.showPrivations === 'boolean' ? source.showPrivations : true,
    };
  };

  const applyNormalizedCoversheetState = (normalized) => {
    setIsUrgent(normalized.isUrgent);
    setIsParPorteur(normalized.isParPorteur);
    setIsMineurEnCause(normalized.isMineurEnCause);
    setDateTransmission(normalized.dateTransmission);
    setNombreAnnexes(normalized.nombreAnnexes);
    setPhenomene(normalized.phenomene);
    setMentionPersonnelle(normalized.mentionPersonnelle);
    setSelectedRegistre(normalized.selectedRegistre);
    setSelectedType(normalized.selectedType);
    setSelectedRecipients(normalized.selectedRecipients);
    setSelectedSubjects(normalized.selectedSubjects);
    setAvisMagistrats(normalized.avisMagistrats);
    setPrivationsLibertes(normalized.privationsLibertes);
    setSelectedFaits(normalized.selectedFaits);
    setRelationSuspectVictime(normalized.relationSuspectVictime);
    setSelectedPersonnesPhysiques(normalized.selectedPersonnesPhysiques);
    setSelectedPersonnesMorales(normalized.selectedPersonnesMorales);
    setSectionsPersonnalisees(normalized.sectionsPersonnalisees);
    setShowSujet(normalized.showSujet);
    setShowAvisMagistrat(normalized.showAvisMagistrat);
    setShowPrivations(normalized.showPrivations);
  };

  const getCurrentCoversheetPayload = () => ({
    isUrgent,
    isParPorteur,
    isMineurEnCause,
    dateTransmission,
    nombreAnnexes,
    phenomene,
    mentionPersonnelle,
    selectedRegistre,
    selectedType,
    selectedRecipients,
    selectedSubjects,
    avisMagistrats,
    privationsLibertes,
    selectedFaits,
    relationSuspectVictime,
    selectedPersonnesPhysiques,
    selectedPersonnesMorales,
    sectionsPersonnalisees,
    showSujet,
    showAvisMagistrat,
    showPrivations
  });

  // Charger les phénomènes depuis l'API
  useEffect(() => {
    const fetchPhenomenes = async () => {
      try {
        const response = await fetch('/api/phenomena?lang=fr');
        if (response.ok) {
          const data = await response.json();
          setPhenomenes(data);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des phénomènes:', error);
      }
    };
    fetchPhenomenes();
  }, []);

  // Charger les registres depuis l'API
  useEffect(() => {
    const fetchRegistres = async () => {
      try {
        const response = await fetch('/api/registers?lang=fr');
        if (response.ok) {
          const data = await response.json();
          setRegistres(data);
          // Sélectionner automatiquement le premier registre s'il y en a
          if (data.length > 0) {
            setSelectedRegistre(data[0].id);
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des registres:', error);
      }
    };
    fetchRegistres();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Charger les types depuis l'API
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const response = await fetch('/api/types?lang=fr');
        if (response.ok) {
          const data = await response.json();
          setTypes(data);
          // Sélectionner automatiquement le premier type s'il y en a
          if (data.length > 0) {
            setSelectedType(data[0].id);
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des types:', error);
      }
    };
    fetchTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Charger les destinataires depuis l'API
  useEffect(() => {
    const fetchRecipients = async () => {
      try {
        const response = await fetch('/api/recipients?lang=fr');
        if (response.ok) {
          const data = await response.json();
          setRecipients(data);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des destinataires:', error);
      }
    };
    fetchRecipients();
  }, []);

  // Charger les sujets depuis l'API
  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const response = await fetch('/api/subjects?lang=fr');
        if (response.ok) {
          const data = await response.json();
          setSubjects(data);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des sujets:', error);
      }
    };
    fetchSubjects();
  }, []);

  // Charger les magistrats depuis l'API
  useEffect(() => {
    const fetchMagistrats = async () => {
      try {
        const response = await fetch('/api/magistrats?lang=fr');
        if (response.ok) {
          const data = await response.json();
          setMagistrats(data);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des magistrats:', error);
      }
    };
    fetchMagistrats();
  }, []);

  // Charger les personnes physiques depuis le rapport actuel
  useEffect(() => {
    const fetchPersonnesPhysiques = async () => {
      if (!report) {
        setPersonnesPhysiques([]);
        setSelectedPersonnesPhysiques({});
        return;
      }

      try {
        const token = sessionStorage.getItem('authToken');
        const response = await axios.get('/api/report/search', {
          params: { number: report.number, year: report.year },
          headers: { Authorization: `Bearer ${token}` },
        });
        
        // Récupérer les personnes physiques du rapport
        const personnes = response.data.personnesPhysiques || [];
        setPersonnesPhysiques(personnes);
        
        // Initialiser toutes les personnes comme non sélectionnées (décochées)
        const initialSelection = {};
        personnes.forEach(personne => {
          initialSelection[personne._id] = false;
        });
        setSelectedPersonnesPhysiques(initialSelection);
      } catch (error) {
        console.error('Erreur lors du chargement des personnes physiques:', error);
        setPersonnesPhysiques([]);
        setSelectedPersonnesPhysiques({});
      }
    };

    fetchPersonnesPhysiques();
  }, [report]);

  // Charger les personnes morales depuis le rapport actuel
  useEffect(() => {
    const fetchPersonnesMorales = async () => {
      if (!report) {
        setPersonnesMorales([]);
        setSelectedPersonnesMorales({});
        return;
      }

      try {
        const token = sessionStorage.getItem('authToken');
        const response = await axios.get('/api/report/search', {
          params: { number: report.number, year: report.year },
          headers: { Authorization: `Bearer ${token}` },
        });
        
        // Récupérer les personnes morales du rapport
        const personnesMoralesData = response.data.personnesMorales || [];
        setPersonnesMorales(personnesMoralesData);
        
        // Initialiser toutes les personnes morales comme non sélectionnées (décochées)
        const initialSelection = {};
        personnesMoralesData.forEach(personne => {
          initialSelection[personne._id] = false;
        });
        setSelectedPersonnesMorales(initialSelection);
      } catch (error) {
        console.error('Erreur lors du chargement des personnes morales:', error);
        setPersonnesMorales([]);
        setSelectedPersonnesMorales({});
      }
    };

    fetchPersonnesMorales();
  }, [report]);

  useEffect(() => {
    if (!report) {
      lastSyncedSnapshotRef.current = null;
    }
  }, [report]);

  // Charger les faits depuis le rapport actuel
  useEffect(() => {
    const fetchFaits = async () => {
      if (!report) {
        setFaits([]);
        setSelectedFaits({});
        return;
      }

      try {
        const token = sessionStorage.getItem('authToken');
        const response = await axios.get('/api/report/search', {
          params: { number: report.number, year: report.year },
          headers: { Authorization: `Bearer ${token}` },
        });
        
        // Récupérer les faits du rapport
        const faitsData = response.data.faits || [];
        setFaits(faitsData);
        
        // Initialiser tous les faits comme non sélectionnés (décochés)
        const initialSelection = {};
        faitsData.forEach(fait => {
          initialSelection[fait._id] = false;
        });
        setSelectedFaits(initialSelection);
      } catch (error) {
        console.error('Erreur lors du chargement des faits:', error);
        setFaits([]);
        setSelectedFaits({});
      }
    };

    fetchFaits();
  }, [report]);

  // Charger les données coversheet du rapport
  useEffect(() => {
    if (!report) return;

    const fetchCoversheet = async () => {
      try {
        const token = sessionStorage.getItem('authToken');
        const response = await axios.get('/api/report/search', {
          params: { number: report.number, year: report.year },
          headers: { Authorization: `Bearer ${token}` },
        });
        
        setReportData(response.data);
        
        const normalizedCoversheet = normalizeCoversheetPayload(
          response.data.coversheet || {}
        );
        applyNormalizedCoversheetState(normalizedCoversheet);
        lastSyncedSnapshotRef.current = JSON.stringify(normalizedCoversheet);
      } catch (error) {
        console.error('Erreur lors du chargement du coversheet:', error);
      }
    };

    fetchCoversheet();
  }, [report, registres, types]);

  // Écouter les mises à jour en temps réel via Socket.IO
  useEffect(() => {
    if (!socket || !report) return;

    const handleReportUpdated = (updatedReport) => {
      if (updatedReport.number === report.number && 
          updatedReport.year === report.year) {
        
        // Mettre à jour les faits si présents dans la mise à jour
        if (updatedReport.faits !== undefined) {
          setFaits(updatedReport.faits || []);
          
          // Mettre à jour la sélection pour les nouveaux faits
          setSelectedFaits(prev => {
            const newSelection = { ...prev };
            
            // Ajouter les nouveaux faits avec sélection à false
            (updatedReport.faits || []).forEach(fait => {
              if (newSelection[fait._id] === undefined) {
                newSelection[fait._id] = false;
              }
            });
            
            // Supprimer les faits qui n'existent plus
            const faitIds = (updatedReport.faits || []).map(f => f._id);
            Object.keys(newSelection).forEach(id => {
              if (!faitIds.includes(id)) {
                delete newSelection[id];
              }
            });
            
            return newSelection;
          });
        }

        // Mettre à jour les personnes physiques si présentes dans la mise à jour
        if (updatedReport.personnesPhysiques !== undefined) {
          setPersonnesPhysiques(updatedReport.personnesPhysiques || []);
          
          setSelectedPersonnesPhysiques(prev => {
            const newSelection = { ...prev };
            
            (updatedReport.personnesPhysiques || []).forEach(personne => {
              if (newSelection[personne._id] === undefined) {
                newSelection[personne._id] = false;
              }
            });
            
            const personneIds = (updatedReport.personnesPhysiques || []).map(p => p._id);
            Object.keys(newSelection).forEach(id => {
              if (!personneIds.includes(id)) {
                delete newSelection[id];
              }
            });
            
            return newSelection;
          });
        }

        // Mettre à jour les personnes morales si présentes dans la mise à jour
        if (updatedReport.personnesMorales !== undefined) {
          setPersonnesMorales(updatedReport.personnesMorales || []);
          
          setSelectedPersonnesMorales(prev => {
            const newSelection = { ...prev };
            
            (updatedReport.personnesMorales || []).forEach(personne => {
              if (newSelection[personne._id] === undefined) {
                newSelection[personne._id] = false;
              }
            });
            
            const personneIds = (updatedReport.personnesMorales || []).map(p => p._id);
            Object.keys(newSelection).forEach(id => {
              if (!personneIds.includes(id)) {
                delete newSelection[id];
              }
            });
            
            return newSelection;
          });
        }

        // Mettre à jour le coversheet si présent dans la mise à jour
        if (updatedReport.coversheet !== undefined) {
          const normalized = normalizeCoversheetPayload(
            updatedReport.coversheet || {}
          );
          applyNormalizedCoversheetState(normalized);
          lastSyncedSnapshotRef.current = JSON.stringify(normalized);
        }
      }
    };

    socket.on('reportUpdated', handleReportUpdated);

    return () => {
      socket.off('reportUpdated', handleReportUpdated);
    };
  }, [socket, report]);

  useEffect(() => {
    const updateProgress = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const p = docHeight > 0 ? Math.min(1, scrollTop / docHeight) : 0;
      setProgress(p * 100);
    };
    updateProgress();
    const onScroll = () => {
      updateProgress();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const nodes = sections.map((s) => document.getElementById(s.id)).filter(Boolean);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        root: null,
        rootMargin: '0px 0px -70% 0px',
        threshold: 0.3,
      }
    );
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [sections]);

  const handleTabChange = (_event, index) => {
    const id = sections[index].id;
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const activeIndex = Math.max(0, sections.findIndex((s) => s.id === activeId));

  const handleIncrementAnnexes = () => {
    setNombreAnnexes(prev => Math.min(prev + 1, 99));
  };

  const handleDecrementAnnexes = () => {
    setNombreAnnexes(prev => Math.max(prev - 1, 0));
  };

  // Fonctions pour gérer les destinataires
  const handleAddRecipientFromList = (event, value) => {
    if (value && !selectedRecipients.find(r => r.id === value.id)) {
      setSelectedRecipients([...selectedRecipients, { ...value, isCustom: false }]);
      setAutocompleteKey(prev => prev + 1); // Force le re-render de l'Autocomplete
    }
  };

  const handleAddCustomRecipient = () => {
    if (customRecipientName.trim()) {
      const newRecipient = {
        id: `custom-${Date.now()}`,
        nom: customRecipientName.trim(),
        isCustom: true,
      };
      setSelectedRecipients([...selectedRecipients, newRecipient]);
      setCustomRecipientName('');
    }
  };

  const handleRemoveRecipient = (recipientId) => {
    setSelectedRecipients(selectedRecipients.filter(r => r.id !== recipientId));
  };

  const handleCustomRecipientKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddCustomRecipient();
    }
  };

  // Fonctions pour gérer les sujets
  const handleAddSubjectFromList = (event, value) => {
    if (value && !selectedSubjects.find(s => s.id === value.id)) {
      setSelectedSubjects([...selectedSubjects, { ...value, isCustom: false }]);
      setAutocompleteKeySubject(prev => prev + 1);
    }
  };

  const handleAddCustomSubject = () => {
    if (customSubjectName.trim()) {
      const newSubject = {
        id: `custom-${Date.now()}`,
        nom: customSubjectName.trim(),
        isCustom: true,
      };
      setSelectedSubjects([...selectedSubjects, newSubject]);
      setCustomSubjectName('');
    }
  };

  const handleRemoveSubject = (subjectId) => {
    setSelectedSubjects(selectedSubjects.filter(s => s.id !== subjectId));
  };

  const handleCustomSubjectKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddCustomSubject();
    }
  };

  // Fonctions pour gérer les privations de liberté
  const handleAddPrivation = () => {
    if (!selectedPersonneId || !dateHeurePrivation || !typePrivation) {
      return;
    }
    
    const newPrivation = {
      id: `privation-${Date.now()}`,
      personneId: selectedPersonneId, // Seulement l'ID
      dateHeure: dateHeurePrivation,
      type: typePrivation,
    };
    
    setPrivationsLibertes([...privationsLibertes, newPrivation]);
    
    // Réinitialiser le formulaire
    setSelectedPersonneId('');
    setDateHeurePrivation('');
    setTypePrivation('');
  };

  const handleRemovePrivation = (privationId) => {
    setPrivationsLibertes(privationsLibertes.filter(p => p.id !== privationId));
  };

  const formatNaissance = (personne) => {
    if (!personne || !personne.birthdate) return '';
    const dateNaissance = new Date(personne.birthdate).toLocaleDateString('fr-FR');
    const ne = personne.gender === 'F' ? 'née' : 'né';
    return ` (${ne} le ${dateNaissance})`;
  };

  const formatDateHeurePrivation = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const dateFormatee = date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    const heures = date.getHours();
    const heuresFormatees = heures.toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const heureTexte = heures >= 2 ? 'heures' : 'heure';
    return `${dateFormatee} à ${heuresFormatees}:${minutes} ${heureTexte}`;
  };

  // Fonctions pour gérer les faits
  const handleToggleFait = (faitId) => {
    setSelectedFaits(prev => ({
      ...prev,
      [faitId]: !prev[faitId]
    }));
  };

  const formatFaitDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fonctions pour gérer les personnes physiques
  const handleTogglePersonnePhysique = (personneId) => {
    setSelectedPersonnesPhysiques(prev => ({
      ...prev,
      [personneId]: !prev[personneId]
    }));
  };

  // Fonctions pour gérer les personnes morales
  const handleTogglePersonneMorale = (personneId) => {
    setSelectedPersonnesMorales(prev => ({
      ...prev,
      [personneId]: !prev[personneId]
    }));
  };

  // Fonctions pour gérer les sections personnalisées
  const handleAddSection = () => {
    if (!titreSection.trim() || !contenuSection.trim()) {
      return;
    }

    if (editingSectionId) {
      // Mode édition
      setSectionsPersonnalisees(sectionsPersonnalisees.map(section =>
        section.id === editingSectionId
          ? { ...section, titre: titreSection.trim(), contenu: contenuSection.trim() }
          : section
      ));
      setEditingSectionId(null);
    } else {
      // Mode ajout
      const newSection = {
        id: `section-${Date.now()}`,
        titre: titreSection.trim(),
        contenu: contenuSection.trim(),
      };
      setSectionsPersonnalisees([...sectionsPersonnalisees, newSection]);
    }

    // Réinitialiser le formulaire
    setTitreSection('');
    setContenuSection('');
  };

  const handleEditSection = (section) => {
    setTitreSection(section.titre);
    setContenuSection(section.contenu);
    setEditingSectionId(section.id);
  };

  const handleCancelEditSection = () => {
    setTitreSection('');
    setContenuSection('');
    setEditingSectionId(null);
  };

  const handleRemoveSection = (sectionId) => {
    setSectionsPersonnalisees(sectionsPersonnalisees.filter(s => s.id !== sectionId));
    if (editingSectionId === sectionId) {
      handleCancelEditSection();
    }
  };

  const handleMoveSectionUp = (index) => {
    if (index === 0) return;
    const newSections = [...sectionsPersonnalisees];
    [newSections[index - 1], newSections[index]] = [newSections[index], newSections[index - 1]];
    setSectionsPersonnalisees(newSections);
  };

  const handleMoveSectionDown = (index) => {
    if (index === sectionsPersonnalisees.length - 1) return;
    const newSections = [...sectionsPersonnalisees];
    [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
    setSectionsPersonnalisees(newSections);
  };

  // Fonctions pour gérer les avis de magistrats
  const handleAddAvisMagistrat = () => {
    if ((!selectedMagistratId && !customMagistratInfo.trim()) || !dateHeureAvis) {
      return;
    }

    let newAvis;
    if (selectedMagistratId) {
      // Magistrat sélectionné depuis la liste
      const magistratSelected = magistrats.find(m => m.id === selectedMagistratId);
      newAvis = {
        id: `avis-${Date.now()}`,
        magistrat: magistratSelected,
        dateHeure: dateHeureAvis,
        isCustom: false,
      };
    } else {
      // Magistrat personnalisé
      newAvis = {
        id: `avis-${Date.now()}`,
        magistrat: {
          id: `custom-${Date.now()}`,
          infoComplete: customMagistratInfo.trim(),
          genre: customMagistratGenre,
        },
        dateHeure: dateHeureAvis,
        isCustom: true,
      };
    }

    setAvisMagistrats([...avisMagistrats, newAvis]);
    
    // Réinitialiser le formulaire
    setSelectedMagistratId('');
    setCustomMagistratInfo('');
    setCustomMagistratGenre('homme');
    setDateHeureAvis('');
    setAutocompleteMagistratKey(prev => prev + 1);
  };

  const handleRemoveAvisMagistrat = (avisId) => {
    setAvisMagistrats(avisMagistrats.filter(a => a.id !== avisId));
  };

  const formatMagistratLabel = (magistrat) => {
    if (!magistrat) return '';
    return `${magistrat.nom?.toUpperCase() || ''} ${magistrat.prenom || ''}, ${magistrat.titre || ''} ${magistrat.parquet || ''}`;
  };

  const formatAvisMagistrat = (avis) => {
    if (!avis) return '';
    
    let texte = '';
    
    // Nom et informations du magistrat
    if (avis.isCustom) {
      texte = avis.magistrat.infoComplete;
    } else {
      const mag = avis.magistrat;
      texte = `${mag.nom?.toUpperCase() || ''} ${mag.prenom || ''}, ${mag.titre || ''} ${mag.parquet || ''}`;
    }
    
    // Détermine si c'est une femme pour accorder "avisé/avisée"
    const estFemme = avis.magistrat.genre === 'femme';
    texte += estFemme ? ' a été avisée le ' : ' a été avisé le ';
    
    // Formater la date et l'heure
    if (avis.dateHeure) {
      const date = new Date(avis.dateHeure);
      const dateFormatee = date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
      const heures = date.getHours();
      const heuresFormatees = heures.toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const heureTexte = heures >= 2 ? 'heures' : 'heure';
      texte += `${dateFormatee} à ${heuresFormatees}:${minutes} ${heureTexte}`;
    }
    
    return texte;
  };

  // Fonction pour sauvegarder le coversheet
  const saveCoversheetRef = useRef();
  
  saveCoversheetRef.current = async () => {
    if (!report || !reportData) return;

    try {
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData._id;
      const coversheetData = getCurrentCoversheetPayload();
      const serializedPayload = JSON.stringify(coversheetData);

      if (lastSyncedSnapshotRef.current === serializedPayload) {
        return;
      }

      await axios.put(
        `/api/report/${reportId}/coversheet`,
        { coversheet: coversheetData },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      lastSyncedSnapshotRef.current = serializedPayload;
      // Pas besoin d'émettre via socket, le serveur le fait déjà
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du coversheet:', error);
    }
  };

  // Debounced save pour éviter trop de requêtes
  const debouncedSave = useRef(null);

  useEffect(() => {
    if (!debouncedSave.current) {
      debouncedSave.current = (() => {
        let timeoutId;
        return () => {
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            if (saveCoversheetRef.current) {
              saveCoversheetRef.current();
            }
          }, 500);
        };
      })();
    }
  }, []);

  // Déclencher la sauvegarde à chaque modification
  useEffect(() => {
    if (report && reportData && debouncedSave.current) {
      debouncedSave.current();
    }
  }, [
    isUrgent, isParPorteur, isMineurEnCause, dateTransmission, nombreAnnexes,
    phenomene, mentionPersonnelle, selectedRegistre, selectedType,
    selectedRecipients, selectedSubjects, avisMagistrats, privationsLibertes,
    selectedFaits, relationSuspectVictime, selectedPersonnesPhysiques,
    selectedPersonnesMorales, sectionsPersonnalisees, showSujet,
    showAvisMagistrat, showPrivations, report, reportData
  ]);

  // Fonction pour obtenir l'état d'affichage d'une section
  const getSectionVisibility = (sectionId) => {
    switch (sectionId) {
      case 'sujet':
        return { isTogglable: true, isVisible: showSujet, setter: setShowSujet };
      case 'avis-magistrat':
        return { isTogglable: true, isVisible: showAvisMagistrat, setter: setShowAvisMagistrat };
      case 'privations':
        return { isTogglable: true, isVisible: showPrivations, setter: setShowPrivations };
      default:
        return { isTogglable: false, isVisible: true, setter: null };
    }
  };

  const renderInfosGeneralesContent = () => (
    <Stack spacing={2.5} sx={{ maxWidth: 800 }}>
      {/* Ligne 1: Statut */}
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" sx={{ minWidth: 120, color: 'text.secondary', fontWeight: 500 }}>
          Statut
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            label="Urgent"
            onClick={() => setIsUrgent(!isUrgent)}
            color={isUrgent ? 'error' : 'default'}
            variant={isUrgent ? 'filled' : 'outlined'}
            size="small"
            sx={{
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontWeight: isUrgent ? 600 : 500,
            }}
          />
          <Chip
            label="Par porteur"
            onClick={() => setIsParPorteur(!isParPorteur)}
            color={isParPorteur ? 'info' : 'default'}
            variant={isParPorteur ? 'filled' : 'outlined'}
            size="small"
            sx={{
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontWeight: isParPorteur ? 600 : 500,
            }}
          />
          <Chip
            label="Mineur en cause"
            onClick={() => setIsMineurEnCause(!isMineurEnCause)}
            color={isMineurEnCause ? 'warning' : 'default'}
            variant={isMineurEnCause ? 'filled' : 'outlined'}
            size="small"
            sx={{
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontWeight: isMineurEnCause ? 600 : 500,
            }}
          />
        </Stack>
      </Stack>

      {/* Ligne : Registre */}
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Typography variant="body2" sx={{ minWidth: 120, color: 'text.secondary', fontWeight: 500 }}>
          Registre
        </Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <Select
            value={selectedRegistre}
            onChange={(e) => setSelectedRegistre(e.target.value)}
          >
            {registres.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.registre}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Ligne : Type de PV */}
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Typography variant="body2" sx={{ minWidth: 120, color: 'text.secondary', fontWeight: 500 }}>
          Type de PV
        </Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <Select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
          >
            {types.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Ligne 2: Nombre d'annexes */}
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Typography variant="body2" sx={{ minWidth: 120, color: 'text.secondary', fontWeight: 500 }}>
          Nombre d'annexes
        </Typography>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            backgroundColor: 'background.paper'
          }}
        >
          <IconButton 
            onClick={handleDecrementAnnexes}
            disabled={nombreAnnexes === 0}
            size="small"
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <Typography 
            sx={{ 
              mx: 2, 
              minWidth: 30, 
              textAlign: 'center',
              fontWeight: 500,
              fontSize: '0.875rem'
            }}
          >
            {nombreAnnexes}
          </Typography>
          <IconButton 
            onClick={handleIncrementAnnexes}
            disabled={nombreAnnexes >= 99}
            size="small"
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      </Stack>

      {/* Ligne 3: Date de transmission */}
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Stack spacing={0.5} sx={{ minWidth: 120 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            Date transmission
          </Typography>
          <Chip 
            label="facultatif" 
            size="small" 
            sx={{ 
              height: 18, 
              fontSize: '0.65rem',
              width: 'fit-content',
              '& .MuiChip-label': {
                px: 1
              }
            }} 
          />
        </Stack>
        <TextField
          type="date"
          value={dateTransmission}
          onChange={(e) => setDateTransmission(e.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={{ width: 200 }}
        />
      </Stack>

      {/* Ligne 4: Phénomène */}
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Stack spacing={0.5} sx={{ minWidth: 120 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            Phénomène
          </Typography>
          <Chip 
            label="facultatif" 
            size="small" 
            sx={{ 
              height: 18, 
              fontSize: '0.65rem',
              width: 'fit-content',
              '& .MuiChip-label': {
                px: 1
              }
            }} 
          />
        </Stack>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <Select
            value={phenomene}
            onChange={(e) => setPhenomene(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">
              <em>Aucun phénomène</em>
            </MenuItem>
            {phenomenes.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.nom}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Ligne 5: Mention personnelle */}
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Stack spacing={0.5} sx={{ minWidth: 120 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, pt: 1 }}>
            Mention perso.
          </Typography>
          <Chip 
            label="facultatif" 
            size="small" 
            sx={{ 
              height: 18, 
              fontSize: '0.65rem',
              width: 'fit-content',
              '& .MuiChip-label': {
                px: 1
              }
            }} 
          />
        </Stack>
        <TextField
          multiline
          rows={2}
          value={mentionPersonnelle}
          onChange={(e) => {
            if (e.target.value.length <= 100) {
              setMentionPersonnelle(e.target.value);
            }
          }}
          placeholder="Maximum 100 caractères"
          size="small"
          sx={{ flex: 1, maxWidth: 500 }}
          helperText={`${mentionPersonnelle.length}/100`}
        />
      </Stack>
    </Stack>
  );

  const renderDestinataresContent = () => (
    <Stack spacing={2} sx={{ maxWidth: 900 }}>
      {/* Section d'ajout de destinataires */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 2, 
          border: 1, 
          borderColor: 'divider',
          borderRadius: 2,
          backgroundColor: 'background.paper'
        }}
      >
        <Stack spacing={2}>
          {/* Sélection depuis la liste */}
          <Box>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 1, 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                fontSize: '0.875rem'
              }}
            >
              <BusinessIcon sx={{ fontSize: '1rem' }} color="primary" />
              Sélectionner depuis la liste
            </Typography>
            <Autocomplete
              key={autocompleteKey}
              options={recipients.filter(r => !selectedRecipients.find(sr => sr.id === r.id))}
              getOptionLabel={(option) => option.nom}
              onChange={handleAddRecipientFromList}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Rechercher un destinataire..."
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                  }}
                />
              )}
              noOptionsText="Aucun destinataire disponible"
              value={null}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.default',
                }
              }}
            />
          </Box>

          <Divider sx={{ my: 0.5 }}>
            <Chip label="OU" size="small" sx={{ fontSize: '0.75rem', height: 22 }} />
          </Divider>

          {/* Ajout personnalisé */}
          <Box>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 1, 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                fontSize: '0.875rem'
              }}
            >
              <PersonAddIcon sx={{ fontSize: '1rem' }} color="primary" />
              Ajouter un destinataire personnalisé
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                size="small"
                placeholder="Nom du destinataire..."
                value={customRecipientName}
                onChange={(e) => setCustomRecipientName(e.target.value)}
                onKeyPress={handleCustomRecipientKeyPress}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'background.default',
                  }
                }}
              />
              <Button
                variant="contained"
                onClick={handleAddCustomRecipient}
                disabled={!customRecipientName.trim()}
                startIcon={<AddIcon fontSize="small" />}
                sx={{ 
                  minWidth: 100,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.875rem'
                }}
              >
                Ajouter
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {/* Liste des destinataires sélectionnés */}
      {selectedRecipients.length > 0 && (
        <Box>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 1.25, 
              fontWeight: 600,
              color: 'text.primary',
              fontSize: '0.875rem'
            }}
          >
            Destinataires sélectionnés ({selectedRecipients.length})
          </Typography>
          <Paper 
            elevation={0}
            sx={{ 
              p: 1.5, 
              border: 1, 
              borderColor: 'primary.main',
              borderRadius: 2,
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(33, 150, 243, 0.08)' 
                : 'rgba(33, 150, 243, 0.04)'
            }}
          >
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {selectedRecipients.map((recipient) => (
                <Chip
                  key={recipient.id}
                  label={recipient.nom}
                  onDelete={() => handleRemoveRecipient(recipient.id)}
                  deleteIcon={<CloseIcon sx={{ fontSize: '1rem' }} />}
                  icon={recipient.isCustom ? <PersonAddIcon sx={{ fontSize: '1rem' }} /> : <BusinessIcon sx={{ fontSize: '1rem' }} />}
                  color={recipient.isCustom ? 'secondary' : 'primary'}
                  variant="filled"
                  size="small"
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.8125rem',
                    height: 28,
                    '& .MuiChip-deleteIcon': {
                      '&:hover': {
                        color: 'error.main',
                      }
                    },
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: theme.shadows[2],
                    }
                  }}
                />
              ))}
            </Stack>
          </Paper>
        </Box>
      )}

      {/* Message si aucun destinataire */}
      {selectedRecipients.length === 0 && (
        <Paper 
          elevation={0}
          sx={{ 
            p: 2, 
            border: 1, 
            borderColor: 'divider',
            borderRadius: 2,
            backgroundColor: 'action.hover',
            textAlign: 'center'
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
            Aucun destinataire sélectionné. Ajoutez des destinataires depuis la liste ou créez-en de nouveaux.
          </Typography>
        </Paper>
      )}
    </Stack>
  );

  const renderSujetContent = () => (
    <Stack spacing={2} sx={{ maxWidth: 900 }}>
      {/* Section d'ajout de sujets */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 2, 
          border: 1, 
          borderColor: 'divider',
          borderRadius: 2,
          backgroundColor: 'background.paper'
        }}
      >
        <Stack spacing={2}>
          {/* Sélection depuis la liste */}
          <Box>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 1, 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                fontSize: '0.875rem'
              }}
            >
              <BusinessIcon sx={{ fontSize: '1rem' }} color="primary" />
              Sélectionner depuis la liste
            </Typography>
            <Autocomplete
              key={autocompleteKeySubject}
              options={subjects.filter(s => !selectedSubjects.find(ss => ss.id === s.id))}
              getOptionLabel={(option) => option.nom}
              onChange={handleAddSubjectFromList}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Rechercher un sujet..."
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                  }}
                />
              )}
              noOptionsText="Aucun sujet disponible"
              value={null}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.default',
                }
              }}
            />
          </Box>

          <Divider sx={{ my: 0.5 }}>
            <Chip label="OU" size="small" sx={{ fontSize: '0.75rem', height: 22 }} />
          </Divider>

          {/* Ajout personnalisé */}
          <Box>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 1, 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                fontSize: '0.875rem'
              }}
            >
              <PersonAddIcon sx={{ fontSize: '1rem' }} color="primary" />
              Ajouter un sujet personnalisé
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                size="small"
                placeholder="Nom du sujet..."
                value={customSubjectName}
                onChange={(e) => setCustomSubjectName(e.target.value)}
                onKeyPress={handleCustomSubjectKeyPress}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'background.default',
                  }
                }}
              />
              <Button
                variant="contained"
                onClick={handleAddCustomSubject}
                disabled={!customSubjectName.trim()}
                startIcon={<AddIcon fontSize="small" />}
                sx={{ 
                  minWidth: 100,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.875rem'
                }}
              >
                Ajouter
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {/* Liste des sujets sélectionnés */}
      {selectedSubjects.length > 0 && (
        <Box>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 1.25, 
              fontWeight: 600,
              color: 'text.primary',
              fontSize: '0.875rem'
            }}
          >
            Sujets sélectionnés ({selectedSubjects.length})
          </Typography>
          <Paper 
            elevation={0}
            sx={{ 
              p: 1.5, 
              border: 1, 
              borderColor: 'primary.main',
              borderRadius: 2,
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(33, 150, 243, 0.08)' 
                : 'rgba(33, 150, 243, 0.04)'
            }}
          >
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {selectedSubjects.map((subject) => (
                <Chip
                  key={subject.id}
                  label={subject.nom}
                  onDelete={() => handleRemoveSubject(subject.id)}
                  deleteIcon={<CloseIcon sx={{ fontSize: '1rem' }} />}
                  icon={subject.isCustom ? <PersonAddIcon sx={{ fontSize: '1rem' }} /> : <BusinessIcon sx={{ fontSize: '1rem' }} />}
                  color={subject.isCustom ? 'secondary' : 'primary'}
                  variant="filled"
                  size="small"
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.8125rem',
                    height: 28,
                    '& .MuiChip-deleteIcon': {
                      '&:hover': {
                        color: 'error.main',
                      }
                    },
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: theme.shadows[2],
                    }
                  }}
                />
              ))}
            </Stack>
          </Paper>
        </Box>
      )}

      {/* Message si aucun sujet */}
      {selectedSubjects.length === 0 && (
        <Paper 
          elevation={0}
          sx={{ 
            p: 2, 
            border: 1, 
            borderColor: 'divider',
            borderRadius: 2,
            backgroundColor: 'action.hover',
            textAlign: 'center'
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
            Aucun sujet sélectionné. Ajoutez des sujets depuis la liste ou créez-en de nouveaux.
          </Typography>
        </Paper>
      )}
    </Stack>
  );

  const renderPrivationsContent = () => (
    <Stack spacing={2} sx={{ maxWidth: 900 }}>
      {/* Formulaire d'ajout de privation */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 2, 
          border: 1, 
          borderColor: 'divider',
          borderRadius: 2,
          backgroundColor: 'background.paper'
        }}
      >
        <Stack spacing={2}>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 1, 
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              fontSize: '0.875rem'
            }}
          >
            <AddIcon sx={{ fontSize: '1rem' }} color="primary" />
            Ajouter une privation de liberté
          </Typography>

          {/* Sélection de la personne */}
          <FormControl fullWidth size="small">
            <InputLabel>Personne privée de liberté</InputLabel>
            <Select
              value={selectedPersonneId}
              label="Personne privée de liberté"
              onChange={(e) => setSelectedPersonneId(e.target.value)}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.default',
                }
              }}
            >
              {personnesPhysiques.length === 0 ? (
                <MenuItem disabled value="">
                  <em>Aucune personne physique disponible</em>
                </MenuItem>
              ) : (
                personnesPhysiques.map((personne) => (
                  <MenuItem key={personne._id} value={personne._id}>
                    {personne.lastname?.toUpperCase() || ''} {personne.firstname || ''}
                    {formatNaissance(personne)}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {/* Date et heure + Type sur la même ligne */}
          <Stack direction="row" spacing={1}>
            <TextField
              label="Date et heure de privation"
              type="datetime-local"
              value={dateHeurePrivation}
              onChange={(e) => setDateHeurePrivation(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              size="small"
              sx={{ 
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.default',
                }
              }}
            />

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={typePrivation}
                label="Type"
                onChange={(e) => setTypePrivation(e.target.value)}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'background.default',
                  }
                }}
              >
                <MenuItem value="judiciairement">Judiciairement</MenuItem>
                <MenuItem value="administrativement">Administrativement</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="contained"
              onClick={handleAddPrivation}
              disabled={!selectedPersonneId || !dateHeurePrivation || !typePrivation}
              startIcon={<AddIcon fontSize="small" />}
              sx={{ 
                minWidth: 100,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem'
              }}
            >
              Ajouter
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Liste des privations de liberté */}
      {privationsLibertes.length > 0 && (
        <Box>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 1.25, 
              fontWeight: 600,
              color: 'text.primary',
              fontSize: '0.875rem'
            }}
          >
            Privations de liberté enregistrées ({privationsLibertes.length})
          </Typography>
          <Paper 
            elevation={0}
            sx={{ 
              p: 1.5, 
              border: 1, 
              borderColor: 'primary.main',
              borderRadius: 2,
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(33, 150, 243, 0.08)' 
                : 'rgba(33, 150, 243, 0.04)'
            }}
          >
            <Stack spacing={1.5}>
              {privationsLibertes.map((privation) => {
                // Résoudre la personne depuis personnesPhysiques par ID
                const personneSelected = personnesPhysiques.find(p => p._id === privation.personneId);
                
                return (
                  <Paper
                    key={privation.id}
                    elevation={1}
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      border: 1,
                      borderColor: 'divider',
                      backgroundColor: 'background.paper',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[3],
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {personneSelected?.lastname?.toUpperCase() || 'Personne supprimée'} {personneSelected?.firstname || ''}
                          {personneSelected && formatNaissance(personneSelected)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', mb: 0.5 }}>
                          {formatDateHeurePrivation(privation.dateHeure)}
                        </Typography>
                        <Chip
                          label={privation.type}
                          size="small"
                          color={privation.type === 'judiciairement' ? 'primary' : 'secondary'}
                          sx={{ 
                            height: 22,
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}
                        />
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleRemovePrivation(privation.id)}
                        sx={{ 
                          color: 'error.main',
                          '&:hover': {
                            backgroundColor: 'error.light',
                            color: 'error.dark',
                          }
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          </Paper>
        </Box>
      )}

      {/* Message si aucune privation */}
      {privationsLibertes.length === 0 && (
        <Paper 
          elevation={0}
          sx={{ 
            p: 2, 
            border: 1, 
            borderColor: 'divider',
            borderRadius: 2,
            backgroundColor: 'action.hover',
            textAlign: 'center'
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
            Aucune privation de liberté enregistrée. Utilisez le formulaire ci-dessus pour en ajouter.
          </Typography>
        </Paper>
      )}
    </Stack>
  );

  const renderPartiesContent = () => (
    <Stack spacing={3} sx={{ maxWidth: 900 }}>
      {/* Champ Relation suspect/victime */}
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Stack spacing={0.5} sx={{ minWidth: 120 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            Relation suspect/victime
          </Typography>
          <Chip 
            label="facultatif" 
            size="small" 
            sx={{ 
              height: 18, 
              fontSize: '0.65rem',
              width: 'fit-content',
              '& .MuiChip-label': {
                px: 1
              }
            }} 
          />
        </Stack>
        <TextField
          value={relationSuspectVictime}
          onChange={(e) => setRelationSuspectVictime(e.target.value)}
          placeholder="Ex: Conjoints, collègues, voisins..."
          size="small"
          sx={{ flex: 1, maxWidth: 500 }}
        />
      </Stack>

      {/* Personnes Physiques */}
      <Box>
        
        {personnesPhysiques.length > 0 ? (
          <>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 1.25, 
                fontWeight: 600,
                color: 'text.primary',
                fontSize: '0.875rem'
              }}
            >
              Sélectionnez les personnes physiques à inclure dans l'émargement parmi les {personnesPhysiques.length} personne(s) enregistrée(s) :
            </Typography>
            <Stack spacing={1.5}>
              {personnesPhysiques.map((personne) => {
                const isSelected = selectedPersonnesPhysiques[personne._id] || false;
                
                return (
                  <Paper
                    key={personne._id}
                    elevation={isSelected ? 2 : 1}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: 1,
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      backgroundColor: isSelected ? 'background.paper' : 'action.hover',
                      opacity: isSelected ? 1 : 0.6,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[3],
                        opacity: isSelected ? 1 : 0.8,
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      {/* Switch de sélection */}
                      <FormControlLabel
                        control={
                          <Switch
                            checked={isSelected}
                            onChange={() => handleTogglePersonnePhysique(personne._id)}
                            color="primary"
                            size="small"
                          />
                        }
                        label=""
                        sx={{ m: 0 }}
                      />
                      
                      {/* Contenu de la personne */}
                      <Box sx={{ flex: 1 }}>
                        {personne.isUnknown ? (
                          <>
                            {/* Personne inconnue */}
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 600,
                                color: isSelected ? 'text.primary' : 'text.secondary',
                                mb: 0.5,
                                fontSize: '0.875rem',
                                fontStyle: 'italic'
                              }}
                            >
                              Inconnu
                            </Typography>
                            
                            {/* Ligne 2: Qualité */}
                            {personne.qualite && (
                              <Chip
                                label={personne.qualite}
                                size="small"
                                color={personne.qualite.toLowerCase().includes('suspect') ? 'error' : 'info'}
                                sx={{ 
                                  mb: 0.5,
                                  height: 22,
                                  fontSize: '0.75rem',
                                  fontWeight: 500
                                }}
                              />
                            )}
                          </>
                        ) : (
                          <>
                            {/* Ligne 1: Nom et prénom */}
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 600,
                                color: isSelected ? 'text.primary' : 'text.secondary',
                                mb: 0.5,
                                fontSize: '0.875rem'
                              }}
                            >
                              {personne.lastname?.toUpperCase() || ''} {personne.firstname || ''}
                              {personne.otherFirstnames && ` (${personne.otherFirstnames})`}
                            </Typography>
                            
                            {/* Ligne 2: Qualité */}
                            {personne.qualite && (
                              <Chip
                                label={personne.qualite}
                                size="small"
                                color={personne.qualite.toLowerCase().includes('suspect') ? 'error' : 'info'}
                                sx={{ 
                                  mb: 0.5,
                                  height: 22,
                                  fontSize: '0.75rem',
                                  fontWeight: 500
                                }}
                              />
                            )}
                            
                            {/* Ligne 3: Date de naissance et lieu */}
                            {(personne.birthdate || personne.birthPlace) && (
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: isSelected ? 'text.secondary' : 'text.disabled',
                                  mb: 0.5,
                                  fontSize: '0.8125rem'
                                }}
                              >
                                {personne.birthdate && `${personne.gender === 'F' ? 'Née' : 'Né'} le ${new Date(personne.birthdate).toLocaleDateString('fr-FR')}`}
                                {personne.birthPlace && ` à ${personne.birthPlace}`}
                              </Typography>
                            )}
                          </>
                        )}
                        
                        {/* Indicateur de sélection */}
                        {isSelected && (
                          <Chip
                            label="Sélectionné"
                            size="small"
                            color="primary"
                            sx={{ 
                              mt: 1,
                              height: 22,
                              fontSize: '0.75rem',
                              fontWeight: 500
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          </>
        ) : (
          <Paper 
            elevation={0}
            sx={{ 
              p: 2, 
              border: 1, 
              borderColor: 'divider',
              borderRadius: 2,
              backgroundColor: 'action.hover',
              textAlign: 'center'
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
              Aucune personne physique enregistrée dans ce rapport.
            </Typography>
          </Paper>
        )}
      </Box>


      {/* Personnes Morales */}
      <Box>
        
        {personnesMorales.length > 0 ? (
          <>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 1.25, 
                fontWeight: 600,
                color: 'text.primary',
                fontSize: '0.875rem'
              }}
            >
              Sélectionnez les personnes morales à inclure dans l'émargement parmi les {personnesMorales.length} personne(s) morale(s) enregistrée(s) :
            </Typography>
            <Stack spacing={1.5}>
              {personnesMorales.map((personne) => {
                const isSelected = selectedPersonnesMorales[personne._id] || false;
                
                return (
                  <Paper
                    key={personne._id}
                    elevation={isSelected ? 2 : 1}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: 1,
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      backgroundColor: isSelected ? 'background.paper' : 'action.hover',
                      opacity: isSelected ? 1 : 0.6,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[3],
                        opacity: isSelected ? 1 : 0.8,
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      {/* Switch de sélection */}
                      <FormControlLabel
                        control={
                          <Switch
                            checked={isSelected}
                            onChange={() => handleTogglePersonneMorale(personne._id)}
                            color="primary"
                            size="small"
                          />
                        }
                        label=""
                        sx={{ m: 0 }}
                      />
                      
                      {/* Contenu de la personne morale */}
                      <Box sx={{ flex: 1 }}>
                        {/* Ligne 1: Dénomination */}
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 600,
                            color: isSelected ? 'text.primary' : 'text.secondary',
                            mb: 0.5,
                            fontSize: '0.875rem'
                          }}
                        >
                          {personne.denomination || 'Dénomination non spécifiée'}
                        </Typography>
                        
                        {/* Ligne 2: Qualité */}
                        {personne.qualite && (
                          <Chip
                            label={personne.qualite}
                            size="small"
                            color="info"
                            sx={{ 
                              mb: 0.5,
                              height: 22,
                              fontSize: '0.75rem',
                              fontWeight: 500
                            }}
                          />
                        )}
                        
                        {/* Ligne 3: Numéro d'entreprise */}
                        {personne.companyNumber && (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: isSelected ? 'text.secondary' : 'text.disabled',
                              mb: 0.5,
                              fontSize: '0.8125rem'
                            }}
                          >
                            N° d'entreprise : {personne.companyNumber}
                          </Typography>
                        )}
                        
                        {/* Indicateur de sélection */}
                        {isSelected && (
                          <Chip
                            label="Sélectionné"
                            size="small"
                            color="primary"
                            sx={{ 
                              mt: 1,
                              height: 22,
                              fontSize: '0.75rem',
                              fontWeight: 500
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          </>
        ) : (
          <Paper 
            elevation={0}
            sx={{ 
              p: 2, 
              border: 1, 
              borderColor: 'divider',
              borderRadius: 2,
              backgroundColor: 'action.hover',
              textAlign: 'center'
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
              Aucune personne morale enregistrée dans ce rapport.
            </Typography>
          </Paper>
        )}
      </Box>
    </Stack>
  );

  const renderAvisMagistratContent = () => (
    <Stack spacing={2} sx={{ maxWidth: 900 }}>
      {/* Formulaire d'ajout d'avis magistrat */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 2, 
          border: 1, 
          borderColor: 'divider',
          borderRadius: 2,
          backgroundColor: 'background.paper'
        }}
      >
        <Stack spacing={2}>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 1, 
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              fontSize: '0.875rem'
            }}
          >
            <AddIcon sx={{ fontSize: '1rem' }} color="primary" />
            Ajouter un avis de magistrat
          </Typography>

          {/* Sélection depuis la liste */}
          <Box>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 1, 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                fontSize: '0.875rem'
              }}
            >
              <BusinessIcon sx={{ fontSize: '1rem' }} color="primary" />
              Sélectionner depuis la liste
            </Typography>
            <Autocomplete
              key={autocompleteMagistratKey}
              options={magistrats}
              getOptionLabel={(option) => formatMagistratLabel(option)}
              onChange={(event, value) => {
                if (value) {
                  setSelectedMagistratId(value.id);
                  setCustomMagistratInfo(''); // Désactiver le champ personnalisé
                  setCustomMagistratGenre('homme'); // Réinitialiser le genre
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Rechercher un magistrat..."
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                  }}
                />
              )}
              noOptionsText="Aucun magistrat disponible"
              value={null}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.default',
                }
              }}
            />
          </Box>

          <Divider sx={{ my: 0.5 }}>
            <Chip label="OU" size="small" sx={{ fontSize: '0.75rem', height: 22 }} />
          </Divider>

          {/* Ajout personnalisé */}
          <Box>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 1, 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                fontSize: '0.875rem'
              }}
            >
              <PersonAddIcon sx={{ fontSize: '1rem' }} color="primary" />
              Ajouter un magistrat personnalisé
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                size="small"
                placeholder="Ex: DUPONT Jean, Procureur du Roi près le Parquet de Bruxelles"
                value={customMagistratInfo}
                onChange={(e) => {
                  setCustomMagistratInfo(e.target.value);
                  if (e.target.value.trim()) {
                    setSelectedMagistratId(''); // Désactiver la sélection depuis la liste
                  }
                }}
                helperText="Entrez le nom, prénom, titre et parquet en un seul champ"
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'background.default',
                  }
                }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Genre</InputLabel>
                <Select
                  value={customMagistratGenre}
                  label="Genre"
                  onChange={(e) => setCustomMagistratGenre(e.target.value)}
                  sx={{ 
                    backgroundColor: 'background.default',
                  }}
                >
                  <MenuItem value="homme">Homme</MenuItem>
                  <MenuItem value="femme">Femme</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Box>

          {/* Date et heure + Bouton Ajouter */}
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              label="Date et heure de l'avis"
              type="datetime-local"
              value={dateHeureAvis}
              onChange={(e) => setDateHeureAvis(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              size="small"
              sx={{ 
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.default',
                }
              }}
            />

            <Button
              variant="contained"
              onClick={handleAddAvisMagistrat}
              disabled={(!selectedMagistratId && !customMagistratInfo.trim()) || !dateHeureAvis}
              startIcon={<AddIcon fontSize="small" />}
              sx={{ 
                minWidth: 100,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
                mt: 0.5,
              }}
            >
              Ajouter
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Liste des avis de magistrats */}
      {avisMagistrats.length > 0 && (
        <Box>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 1.25, 
              fontWeight: 600,
              color: 'text.primary',
              fontSize: '0.875rem'
            }}
          >
            Avis enregistrés ({avisMagistrats.length})
          </Typography>
          <Paper 
            elevation={0}
            sx={{ 
              p: 1.5, 
              border: 1, 
              borderColor: 'primary.main',
              borderRadius: 2,
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(33, 150, 243, 0.08)' 
                : 'rgba(33, 150, 243, 0.04)'
            }}
          >
            <Stack spacing={1.5}>
              {avisMagistrats.map((avis) => (
                <Paper
                  key={avis.id}
                  elevation={1}
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    border: 1,
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: theme.shadows[3],
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 400, 
                          lineHeight: 1.6,
                          color: 'text.primary',
                        }}
                      >
                        {formatAvisMagistrat(avis)}
                      </Typography>
                      {!avis.isCustom && (
                        <Chip
                          label="Magistrat listé"
                          size="small"
                          color="primary"
                          sx={{ 
                            mt: 1,
                            height: 22,
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}
                        />
                      )}
                      {avis.isCustom && (
                        <Chip
                          label="Magistrat personnalisé"
                          size="small"
                          color="secondary"
                          sx={{ 
                            mt: 1,
                            height: 22,
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}
                        />
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveAvisMagistrat(avis.id)}
                      sx={{ 
                        color: 'error.main',
                        '&:hover': {
                          backgroundColor: 'error.light',
                          color: 'error.dark',
                        }
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Paper>
        </Box>
      )}

      {/* Message si aucun avis */}
      {avisMagistrats.length === 0 && (
        <Paper 
          elevation={0}
          sx={{ 
            p: 2, 
            border: 1, 
            borderColor: 'divider',
            borderRadius: 2,
            backgroundColor: 'action.hover',
            textAlign: 'center'
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
            Aucun avis de magistrat enregistré. Utilisez le formulaire ci-dessus pour en ajouter.
          </Typography>
        </Paper>
      )}
    </Stack>
  );

  const renderFaitsContent = () => (
    <Stack spacing={2} sx={{ maxWidth: 900 }}>
      {/* Liste des faits */}
      {faits.length > 0 ? (
        <Box>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 1.25, 
              fontWeight: 600,
              color: 'text.primary',
              fontSize: '0.875rem'
            }}
          >
            Selectionnez les faits à inclure dans l'émargement parmis les {faits.length} fait(s) enregistré(s) :
          </Typography>
          <Stack spacing={1.5}>
            {faits.map((fait) => {
              const isSelected = selectedFaits[fait._id] || false;
              
              return (
                <Paper
                  key={fait._id}
                  elevation={isSelected ? 2 : 1}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: 1,
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    backgroundColor: isSelected ? 'background.paper' : 'action.hover',
                    opacity: isSelected ? 1 : 0.6,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: theme.shadows[3],
                      opacity: isSelected ? 1 : 0.8,
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    {/* Switch de sélection */}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isSelected}
                          onChange={() => handleToggleFait(fait._id)}
                          color="primary"
                          size="small"
                        />
                      }
                      label=""
                      sx={{ m: 0 }}
                    />
                    
                    {/* Contenu du fait */}
                    <Box sx={{ flex: 1 }}>
                      {fait.isUndetermined ? (
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 600,
                            color: isSelected ? 'text.primary' : 'text.secondary',
                            fontSize: '0.875rem'
                          }}
                        >
                          Fait non déterminé - procès-verbal subséquent
                        </Typography>
                      ) : (
                        <>
                          {/* Ligne 1: Description du fait */}
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 600,
                              color: isSelected ? 'text.primary' : 'text.secondary',
                              mb: 0.5,
                              fontSize: '0.875rem'
                            }}
                          >
                            {fait.isQualified && 'Fait qualifié de : '}
                            {fait.isTentative && 'Tentative de : '}
                            {fait.fait}
                          </Typography>
                          
                          {/* Ligne 2: Article si rempli */}
                          {fait.article && (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: isSelected ? 'text.secondary' : 'text.disabled',
                                mb: 0.5,
                                fontSize: '0.8125rem'
                              }}
                            >
                              Article : {fait.article}
                            </Typography>
                          )}
                          
                          {/* Ligne 3: Date/heure */}
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: isSelected ? 'text.secondary' : 'text.disabled',
                              mb: 0.5,
                              fontSize: '0.8125rem'
                            }}
                          >
                            {fait.timeType === 'range' ? (
                              <>
                                Entre le {formatFaitDateTime(fait.rangeLowDateTime)} et le {formatFaitDateTime(fait.rangeHighDateTime)}
                              </>
                            ) : (
                              <>
                                Le {formatFaitDateTime(fait.fixedDateTime)}
                              </>
                            )}
                          </Typography>
                          
                          {/* Ligne 4: Adresse */}
                          {fait.address && (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: isSelected ? 'text.secondary' : 'text.disabled',
                                fontSize: '0.8125rem'
                              }}
                            >
                              À {fait.address}
                            </Typography>
                          )}
                        </>
                      )}
                      
                      {/* Indicateur de sélection */}
                      {isSelected && (
                        <Chip
                          label="Sélectionné"
                          size="small"
                          color="primary"
                          sx={{ 
                            mt: 1,
                            height: 22,
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        </Box>
      ) : (
        <Paper 
          elevation={0}
          sx={{ 
            p: 2, 
            border: 1, 
            borderColor: 'divider',
            borderRadius: 2,
            backgroundColor: 'action.hover',
            textAlign: 'center'
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
            Aucun fait enregistré dans ce rapport. Les faits ajoutés depuis la section Éléments apparaîtront automatiquement ici.
          </Typography>
        </Paper>
      )}
    </Stack>
  );

  const renderSectionsCustomContent = () => (
    <Stack spacing={2} sx={{ maxWidth: 900 }}>
      {/* Formulaire d'ajout/édition de section */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 2, 
          border: 1, 
          borderColor: editingSectionId ? 'warning.main' : 'divider',
          borderRadius: 2,
          backgroundColor: editingSectionId ? 'warning.lighter' : 'background.paper'
        }}
      >
        <Stack spacing={2}>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 1, 
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              fontSize: '0.875rem'
            }}
          >
            {editingSectionId ? (
              <>
                <EditIcon sx={{ fontSize: '1rem' }} color="warning" />
                Modifier la section
              </>
            ) : (
              <>
                <AddIcon sx={{ fontSize: '1rem' }} color="primary" />
                Ajouter une section personnalisée
              </>
            )}
          </Typography>

          {/* Titre */}
          <TextField
            label="Titre de la section"
            value={titreSection}
            onChange={(e) => setTitreSection(e.target.value)}
            size="small"
            fullWidth
            placeholder="Ex: Observations complémentaires, Contexte, etc."
            sx={{ 
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.default',
              }
            }}
          />

          {/* Contenu */}
          <TextField
            label="Contenu"
            value={contenuSection}
            onChange={(e) => setContenuSection(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={4}
            placeholder="Décrivez le contenu de cette section..."
            sx={{ 
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.default',
              }
            }}
          />

          {/* Boutons */}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {editingSectionId && (
              <Button
                variant="outlined"
                onClick={handleCancelEditSection}
                sx={{ 
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.875rem'
                }}
              >
                Annuler
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleAddSection}
              disabled={!titreSection.trim() || !contenuSection.trim()}
              startIcon={editingSectionId ? <EditIcon fontSize="small" /> : <AddIcon fontSize="small" />}
              color={editingSectionId ? 'warning' : 'primary'}
              sx={{ 
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem'
              }}
            >
              {editingSectionId ? 'Modifier' : 'Ajouter'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Liste des sections personnalisées */}
      {sectionsPersonnalisees.length > 0 && (
        <Box>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 1.25, 
              fontWeight: 600,
              color: 'text.primary',
              fontSize: '0.875rem'
            }}
          >
            Sections créées ({sectionsPersonnalisees.length})
          </Typography>
          <Stack spacing={1.5}>
            {sectionsPersonnalisees.map((section, index) => (
              <Paper
                key={section.id}
                elevation={editingSectionId === section.id ? 3 : 1}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: 1,
                  borderColor: editingSectionId === section.id ? 'warning.main' : 'divider',
                  backgroundColor: editingSectionId === section.id ? 'warning.lighter' : 'background.paper',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[3],
                  }
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', color: 'primary.main' }}>
                    {section.titre}
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    <IconButton
                      size="small"
                      onClick={() => handleMoveSectionUp(index)}
                      disabled={index === 0}
                      sx={{ color: 'action.active' }}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleMoveSectionDown(index)}
                      disabled={index === sectionsPersonnalisees.length - 1}
                      sx={{ color: 'action.active' }}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleEditSection(section)}
                      sx={{ color: 'warning.main' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveSection(section.id)}
                      sx={{ 
                        color: 'error.main',
                        '&:hover': {
                          backgroundColor: 'error.light',
                          color: 'error.dark',
                        }
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.6
                  }}
                >
                  {section.contenu}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      {/* Message si aucune section */}
      {sectionsPersonnalisees.length === 0 && (
        <Paper 
          elevation={0}
          sx={{ 
            p: 2, 
            border: 1, 
            borderColor: 'divider',
            borderRadius: 2,
            backgroundColor: 'action.hover',
            textAlign: 'center'
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
            Aucune section personnalisée créée. Utilisez le formulaire ci-dessus pour en ajouter.
          </Typography>
        </Paper>
      )}
    </Stack>
  );

  return (
    <Box
      sx={{
        minHeight: '100%',
        backgroundColor: 'background.default',
      }}
    >
      <Box
        ref={headerRef}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: (t) => t.zIndex.appBar,
          background:
            theme.palette.mode === 'dark'
              ? 'rgb(40, 57, 73)'
              : 'rgb(218, 230, 236)',
        }}
      >
        <Container maxWidth="lg" sx={{ py: 0.5 }}>
          <Tabs
            value={activeIndex}
            onChange={handleTabChange}
            variant="scrollable"
            allowScrollButtonsMobile
            scrollButtons="auto"
            TabIndicatorProps={{
              sx: {
                height: 2,
                borderRadius: 3,
              },
            }}
            sx={{
              minHeight: HEADER_HEIGHT - 8,
              '& .MuiTab-root': {
                minHeight: HEADER_HEIGHT - 16,
                textTransform: 'none',
                fontFamily: 'inherit',
                fontWeight: 500,
                fontSize: '0.875rem',
                mx: 0.2,
                px: 0.8,
                py: 0.5,
                borderRadius: 999,
              },
              '& .MuiTab-root.Mui-selected': {
                fontWeight: 600,
              },
              '& .Mui-selected': {
                color: 'primary.main',
              },
            }}
          >
            {sections.map((s) => (
              <Tab
                key={s.id}
                label={s.label}
                disableRipple
                sx={{
                  color: activeId === s.id ? 'primary.main' : 'text.secondary',
                  backgroundColor:
                    activeId === s.id ? 'action.selected' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              />
            ))}
          </Tabs>
        </Container>
        {/* Barre de progression supprimée à la demande */}
      </Box>

      <Box sx={{ pt: 1 }}>
        {sections.map((s, i) => {
          const visibility = getSectionVisibility(s.id);
          
          return (
            <Box key={s.id}>
              <Box
                id={s.id}
                sx={{
                  scrollMarginTop: `${HEADER_HEIGHT + 12}px`,
                  py: { xs: 5, sm: 7, md: 8 },
                }}
              >
                <Container maxWidth="lg">
                  {/* En-tête de section avec toggle optionnel */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    mb: visibility.isTogglable ? 2 : 2.5,
                    flexWrap: 'wrap',
                    gap: 2
                  }}>
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{ 
                        fontWeight: 500,
                        opacity: visibility.isTogglable && !visibility.isVisible ? 0.5 : 1,
                        transition: 'opacity 0.3s ease',
                      }}
                    >
                      {s.label}
                    </Typography>
                    
                    {visibility.isTogglable && (
                      <Paper
                        elevation={2}
                        sx={{
                          px: 2,
                          py: 1,
                          borderRadius: 3,
                          backgroundColor: visibility.isVisible 
                            ? 'background.paper' 
                            : 'action.hover',
                          border: 1,
                          borderColor: visibility.isVisible 
                            ? 'primary.main' 
                            : 'divider',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            elevation: 4,
                            transform: 'translateY(-1px)',
                            boxShadow: theme.shadows[4],
                          }
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={visibility.isVisible}
                              onChange={(e) => visibility.setter(e.target.checked)}
                              color="primary"
                              size="small"
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {visibility.isVisible ? (
                                <VisibilityIcon fontSize="small" sx={{ color: 'primary.main' }} />
                              ) : (
                                <VisibilityOffIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                              )}
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: visibility.isVisible ? 'text.primary' : 'text.disabled',
                                  fontWeight: 500,
                                }}
                              >
                                {visibility.isVisible ? 'Affichée' : 'Masquée'}
                              </Typography>
                            </Box>
                          }
                          sx={{ 
                            m: 0,
                            '& .MuiFormControlLabel-label': {
                              ml: 1,
                            }
                          }}
                        />
                      </Paper>
                    )}
                  </Box>
                  
                  {/* Contenu de la section */}
                  {(!visibility.isTogglable || visibility.isVisible) && (
                    <Box
                      sx={{
                        opacity: visibility.isVisible ? 1 : 0,
                        transition: 'opacity 0.3s ease',
                      }}
                    >
                      {s.id === 'infos' ? (
                        renderInfosGeneralesContent()
                      ) : s.id === 'destinataires' ? (
                        renderDestinataresContent()
                      ) : s.id === 'sujet' ? (
                        renderSujetContent()
                      ) : s.id === 'avis-magistrat' ? (
                        renderAvisMagistratContent()
                      ) : s.id === 'privations' ? (
                        renderPrivationsContent()
                      ) : s.id === 'faits' ? (
                        renderFaitsContent()
                      ) : s.id === 'parties' ? (
                        renderPartiesContent()
                      ) : s.id === 'sections-custom' ? (
                        renderSectionsCustomContent()
                      ) : (
                        <>
                          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 900 }}>
                            Ceci est un texte d'exemple pour la section « {s.label} ». Vous pouvez y
                            intégrer des formulaires, des composants ou des champs éditables selon vos
                            besoins. L'objectif est d'offrir une lecture confortable et une navigation
                            fluide entre les sections.
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, maxWidth: 900 }}>
                            Astuce UX: utilisez des libellés clairs, des espacements généreux et des
                            éléments interactifs cohérents pour une expérience moderne et soignée.
                          </Typography>
                        </>
                      )}
                    </Box>
                  )}
                </Container>
              </Box>
              {i < sections.length - 1 && (
                <Divider sx={{ my: 0 }} />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default CoverSheet;
